import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getHandleActionKeyDown
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useTables } from '../tables-context';

export function Toggles() {
  const { toggleColor } = useSettings();
  const { activeTable, tables, setActiveTable } = useTables();
  const { id: activeId } = activeTable ?? {};

  if (tables.length < 2) {
    return null;
  }

  return (
    <Container>
      {tables.map(({ id, caption, name }) => {
        const isActive = activeId === id;
        const handleClick = () => setActiveTable(id);

        return (
          <Wrapper
            key={id}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={getHandleActionKeyDown(handleClick)}
            aria-pressed={isActive}
            _color={toggleColor}
          >
            {!!caption && <Caption _color={toggleColor}>{caption}</Caption>}

            <Toggle
              className={createReadableClass({
                component: 'toggles',
                element: 'toggle',
                index: isActive && 'active'
              })}
              _active={isActive}
              _color={toggleColor}
            >
              {name}
            </Toggle>
          </Wrapper>
        );
      })}
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({ component: 'toggles', element: 'container' })
})`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 auto 35px;
`;

const Wrapper = styled.div.attrs({
  className: createReadableClass({ component: 'toggles', element: 'wrapper' })
})<{
  _color: string;
}>`
  position: relative;
  cursor: pointer;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid ${({ _color }) => _color} !important;
    outline-offset: 1px;
  }
`;

const Toggle = styled.div<{
  _active: boolean;
  _color: string;
}>`
  padding: 6px 22px;
  font-size: 15px;
  border-radius: 4px;
  text-align: center;
  width: 100%;
  transition: opacity 0.3s ease;
  min-height: 32px;
  opacity: ${({ _active }) => (_active ? 1 : 0.8)};
  background-color: ${({ _active, _color }) =>
    _active ? getColorWithAlpha(_color, 0.1) : 'transparent'};
  border: 1px solid ${({ _color }) => getColorWithAlpha(_color, 0.1)};
  color: ${({ _color }) => _color};

  &:hover {
    opacity: 1;
  }
`;

const Caption = styled(Typography).attrs({
  textStyle: { fontSize: 12 },
  className: createReadableClass({ component: 'toggles', element: 'caption' })
})<{
  _color: string;
}>`
  max-width: 70%;
  height: auto;
  padding: 4px 8px;
  margin: 0 auto 8px;
  position: relative;
  border-radius: 4px;
  text-align: center;
  color: ${({ _color }) => _color};
  background-color: ${({ _color }) => getColorWithAlpha(_color, 0.1)};

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0.1;
    border-style: solid;
    border-width: 6px 8px 0 8px;
    border-color: currentColor transparent transparent transparent;
  }
`;
